  import {defineStore} from 'pinia';
  import {useFilesStore} from '~/stores/files.js';
  import {DIGITIZATION_FRIENDLY_STATUSES} from '~/utils/mapper-digitization.js';

  export const useDigitizationStore = defineStore('digitization', {
  state: () => ({
    projects: [],
    project: null,
    areProjectsLoading: false,
    isSettingFiles: false,

    transferProjectCheckTimeout: null,
  }),

  actions: {
    async getProjects() {
      if (!this.projects.length && !this.areProjectsLoading) {
        try {
          this.areProjectsLoading = true;
          this.projects = await useDigitizationApiService().getProjectList();
        } finally {
          this.areProjectsLoading = false;
        }
      }

      return this.projects;
    },

    async getProjectDetails({id, shareToken}) {
      this.project = await useDigitizationApiService().getProjectDetails({id, shareToken});
      const {currentUser} = useSessionStore();
      this.project.userNeedsToBuyStorage = !currentUser?.isMember || this.project.total_size_bytes > currentUser.storage_remaining;
      this.project.products = {};

      return this.project;
    },

    async getProjectProducts() {
      const {currentUser} = useSessionStore();

      if (this.project?.complete && this.project.hasStorageNotStarted) {
        let products;

        products = await useDigitizationApiService().getProjectProducts({
          projectSizeBytes: this.project.total_size_bytes,
          storageNeededBytes: currentUser?.isMember ? this.project.total_size_bytes - currentUser.storage_remaining : this.project.total_size_bytes
        });

        this.project.products = {
          storageOneTime: mapPromotions({products: products.storage_one_time}),
          storageInstallment: mapPromotions({products: products.storage_installment}),
          extension: mapPromotions({products: [products.extension]})[0]
        }

      }

    },

    async makeFileCover({projectId, file}) {
      const res = await useDigitizationApiService().updateProjectCover({projectId, file});
      const projectToUpdate = this.projects.find(item => item.id === Number(projectId));
      projectToUpdate.conversion_file = res.conversion_file;

      const avatarImage = (() => {
        if (projectToUpdate.conversion_file?.images?.preview) {
          return updateQueryParameters(projectToUpdate.conversion_file?.images?.preview, {
            height: 350
          });
        }
      })();

      projectToUpdate.avatarImage = avatarImage;

      if (this.project?.id === Number(projectId)) {
        this.project.conversion_file = res.conversion_file;
        this.project.avatarImage = avatarImage;
      }

      return projectToUpdate;
    },

    clearProjects() {
      this.projects = [];
    },

    async refreshTransferStatus({project}) {
      const transferStatus = await useDigitizationApiService().getTransferStatus(project);

      const hasStoragePermanent = transferStatus.status === DIGITIZATION_STORAGE_TRANSFER_STATUSES.complete;
      const hasStorageInProgress = transferStatus.status === DIGITIZATION_STORAGE_TRANSFER_STATUSES.inProgress;
      const hasStorageNotStarted = transferStatus.status === DIGITIZATION_STORAGE_TRANSFER_STATUSES.notStarted;

      const storedProject = this.projects.find(o => o.id === project.id);
      if (storedProject) {
        storedProject.storage_transfer_status = transferStatus.status;
        storedProject.hasStoragePermanent = hasStoragePermanent;
        storedProject.hasStorageInProgress = hasStorageInProgress;
        storedProject.hasStorageNotStarted = hasStorageNotStarted;
        storedProject.album_slug = transferStatus.album_slug;
      }

      if (this.project?.id === project.id) {
        this.project.storage_transfer_status = transferStatus.status;
        this.project.hasStoragePermanent = hasStoragePermanent;
        this.project.hasStorageInProgress = hasStorageInProgress;
        this.project.hasStorageNotStarted = hasStorageNotStarted;
        this.project.album_slug = transferStatus.album_slug;
      }

      if (hasStoragePermanent) {
        useSessionStore().getCurrentUser();
      }

      this.setupTransferProjectCheck();
    },

    setupTransferProjectCheck() {
      if (!process.client || this.transferProjectCheckTimeout) {
        return;
      }

      const transferProject = this.projects.find(o => o.hasStorageInProgress) || (this.project?.hasStorageInProgress ? this.project : null);

      if (transferProject) {
        this.transferProjectCheckTimeout = setTimeout(() => {
          this.transferProjectCheckTimeout = null;
          this.refreshTransferStatus({project: transferProject});
        }, DIGITIZATION_STATUS_CHECK_INTERVAL);
      } else if (this.transferProjectCheckTimeout) {
        clearTimeout(this.transferProjectCheckTimeout);
        this.transferProjectCheckTimeout = null;
      }
    },

    async setProjectFiles() {
      const filesStore = useFilesStore();

      this.isSettingFiles = true;
      await filesStore.clearFiles({preserveDetailFile: true});

      let filesToSet = null;

      try {
        if (!this.project.download_expired && this.project.complete) {
          filesToSet = this.project.line_items;
        } else if (this.project.download_expired && this.project.hasStoragePermanent) {
          filesToSet = await filesStore.getFileDetails({
            files: this.project.line_items,
            refresh: true
          });

          filesToSet = filesToSet.map(file => {
            const originalItem = this.project.line_items.find(f => f.id === file.id);
            return {...file, group: originalItem.group};
          }).sort((a, b) => {
            const aIndex = this.project.line_items.findIndex(f => f.id === a.id);
            const bIndex = this.project.line_items.findIndex(f => f.id === b.id);
            return aIndex - bIndex;
          });

          this.project.line_items = filesToSet;
        }

        if (filesToSet) {
          filesStore.setDigitizationFiles({
            files: filesToSet.map(file => ({
              ...file,
              download_expires_at: this.project.download_expires_at
            }))
          });
        }
      } finally {
        this.isSettingFiles = false;
      }
    }
  }
});
